import { useCallback, useMemo, useState } from 'react';

/** Default number of past entries retained before the oldest is dropped. */
export const DEFAULT_UNDO_LIMIT = 50;

export interface UndoableStateOptions {
  /** Maximum number of past entries kept. Older entries are discarded. */
  limit?: number;
}

export interface UndoableState<T> {
  value: T;
  /** Commit a new value and push the previous one onto the undo stack. */
  set: (next: T | ((prev: T) => T)) => void;
  /**
   * Overwrite the current value without creating an undo entry. Use for
   * high-frequency updates (typing) that should undo as a single step.
   */
  replace: (next: T | ((prev: T) => T)) => void;
  undo: () => void;
  redo: () => void;
  /** Clear both stacks and start over from `value`. */
  reset: (value: T) => void;
  canUndo: boolean;
  canRedo: boolean;
}

function resolve<T>(next: T | ((prev: T) => T), prev: T): T {
  return typeof next === 'function' ? (next as (prev: T) => T)(prev) : next;
}

interface History<T> {
  past: T[];
  present: T;
  future: T[];
}

/**
 * Undo/redo wrapper around a single piece of state.
 *
 * `set` commits a checkpoint, `replace` mutates the current checkpoint in
 * place. Committing a new value always clears the redo stack, matching how
 * editors behave when you type after undoing.
 */
export function useUndoableState<T>(
  initialValue: T,
  { limit = DEFAULT_UNDO_LIMIT }: UndoableStateOptions = {}
): UndoableState<T> {
  const [history, setHistory] = useState<History<T>>(() => ({
    past: [],
    present: initialValue,
    future: [],
  }));

  const set = useCallback((next: T | ((prev: T) => T)) => {
    setHistory(current => {
      const value = resolve(next, current.present);
      if (Object.is(value, current.present)) {
        return current;
      }
      const past = [...current.past, current.present];
      return {
        past: past.length > limit ? past.slice(past.length - limit) : past,
        present: value,
        future: [],
      };
    });
  }, [limit]);

  const replace = useCallback((next: T | ((prev: T) => T)) => {
    setHistory(current => {
      const value = resolve(next, current.present);
      if (Object.is(value, current.present)) {
        return current;
      }
      return { ...current, present: value };
    });
  }, []);

  const undo = useCallback(() => {
    setHistory(current => {
      if (current.past.length === 0) {
        return current;
      }
      return {
        past: current.past.slice(0, -1),
        present: current.past[current.past.length - 1],
        future: [current.present, ...current.future],
      };
    });
  }, []);

  const redo = useCallback(() => {
    setHistory(current => {
      if (current.future.length === 0) {
        return current;
      }
      return {
        past: [...current.past, current.present],
        present: current.future[0],
        future: current.future.slice(1),
      };
    });
  }, []);

  const reset = useCallback((value: T) => {
    setHistory({ past: [], present: value, future: [] });
  }, []);

  return useMemo(() => ({
    value: history.present,
    set,
    replace,
    undo,
    redo,
    reset,
    canUndo: history.past.length > 0,
    canRedo: history.future.length > 0,
  }), [history, set, replace, undo, redo, reset]);
}

export default useUndoableState;
